import { useMemo } from "react";

import type { PlayerSource } from "@/features/player-kanban/components/molecules/PlayerSourceSelector";
import type { ServerGpvRange } from "@/features/player-kanban/utils/playerFilter";

import {
  useKanbanListWatchlistPlayersQuery,
  useKanbanListMyOrganizationsPlayersQuery,
  type KanbanListWatchlistPlayersQuery,
  type KanbanListMyOrganizationsPlayersQuery,
} from "@/generated/graphql";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

/**
 * Props for usePlayerQuery hook
 */
interface UsePlayerQueryProps {
  /**
   * Source of players to query ("roster" or "watchlist")
   */
  playerSource: PlayerSource;
  /**
   * Currently selected watchlist ID or "all" for all watchlists
   */
  watchListSelected: string;
  /**
   * Specific watchlist ID to query when single watchlist is selected
   */
  queryWatchlistId: string;
  /**
   * Array of all available watchlist IDs for "all" watchlists query
   */
  allWatchlistIds: string[];
  /**
   * Active GPV midpoint bounds (euros) forwarded to the server-side
   * `listWatchlistPlayers` filter (SE-6496). Null/undefined when the board's
   * cost filter is inactive. Only applies to the watchlist query — the roster
   * query has no server-side GPV filter and keeps client-side filtering.
   */
  serverGpvRange?: ServerGpvRange | null;
  /**
   * Explicit watchlist ids to read, replacing the `watchListSelected` /
   * `queryWatchlistId` derivation entirely (SE-6554).
   *
   * The add-players drawer filters by a SET of watchlists, not one, and its
   * selection is drawer-local — it must not follow (or disturb) the
   * `watchListSelectedVar` the scouting surfaces share. Because
   * `listWatchlistPlayers` already takes an id array, that is expressible with
   * no new query: the drawer hands the ids straight through.
   *
   * Undefined (the legacy callers) keeps the single-select derivation. An EMPTY
   * array is not a way to say "all" — it skips the read, same as having no
   * watchlists, so callers resolve "no filter" to the full id list themselves.
   */
  selectedWatchlistIds?: readonly string[];
}

/**
 * Hook that handles GraphQL player queries and data processing for both single and "All" watchlists, or roster.
 *
 * Uses a single batch query with `watchlistIds` to fetch players from multiple watchlists
 * instead of issuing separate parallel queries per watchlist (SE-3869).
 * @param root0 - The hook props
 * @param root0.playerSource - Source of players to query, either "roster" for organization players or "watchlist" for watchlist players
 * @param root0.watchListSelected - Currently selected watchlist ID or "all" to aggregate data from all watchlists
 * @param root0.queryWatchlistId - Specific watchlist ID to query when a single watchlist is selected (not "all")
 * @param root0.allWatchlistIds - Array of all available watchlist IDs used when watchListSelected is "all" to batch into a single query
 * @param root0.serverGpvRange - Active GPV bounds (euros) forwarded to the server-side watchlist filter (SE-6496); null when inactive
 * @param root0.selectedWatchlistIds - Explicit watchlist ids to read, replacing the single-select derivation (SE-6554)
 * @returns Object with processedPlayers array, finalPlayersLoading/finalPlayersError states, and loadMore for pagination
 */
const usePlayerQuery = ({
  playerSource,
  watchListSelected,
  queryWatchlistId,
  allWatchlistIds,
  serverGpvRange,
  selectedWatchlistIds,
}: UsePlayerQueryProps) => {
  /** Page size for paginated fetching */
  const PAGE_SIZE = 40;

  // Active team scopes the org-roster query so the sidebar reflects the
  // currently-selected team in Demo (ALL) orgs. HIDDEN-mode users get
  // activeTeamId === null and the resolver falls back to the org join.
  // Gate on isTeamSelectionLoaded so we don't fire with stale `null`
  // while the persisted team-selection state is still hydrating.
  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();

  // Roster query - used when playerSource is "roster"
  const {
    data: rosterData,
    loading: rosterLoading,
    error: rosterError,
    fetchMore: rosterFetchMore,
    networkStatus: rosterNetworkStatus,
  } = useKanbanListMyOrganizationsPlayersQuery({
    variables: { first: PAGE_SIZE },
    skip: playerSource !== "roster" || !isTeamSelectionLoaded,
    notifyOnNetworkStatusChange: true,
  });

  const { loadMore: rosterLoadMore } = useInfiniteScroll({
    pageInfo: rosterData?.listMyOrganizationsPlayers?.pageInfo,
    fetchMore: rosterFetchMore,
    networkStatus: rosterNetworkStatus,
  });

  /** Watchlist IDs for the batch query - single ID or all IDs depending on selection */
  const watchlistIds = useMemo(() => {
    if (playerSource === "roster") {
      return [];
    }
    if (selectedWatchlistIds !== undefined) {
      return [...selectedWatchlistIds];
    }
    if (watchListSelected === "all") {
      return allWatchlistIds;
    }
    return queryWatchlistId ? [queryWatchlistId] : [];
  }, [
    playerSource,
    selectedWatchlistIds,
    watchListSelected,
    allWatchlistIds,
    queryWatchlistId,
  ]);

  // Single batch query for watchlist players (replaces 10+ parallel queries)
  const {
    data: watchlistData,
    loading: watchlistLoading,
    error: watchlistError,
    fetchMore: watchlistFetchMore,
    networkStatus: watchlistNetworkStatus,
  } = useKanbanListWatchlistPlayersQuery({
    variables: {
      watchlistIds,
      first: PAGE_SIZE,
      ...(serverGpvRange
        ? {
            minGeminiPlayerValuation: serverGpvRange.min,
            maxGeminiPlayerValuation: serverGpvRange.max,
          }
        : {}),
    },
    skip: watchlistIds.length === 0,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const { loadMore: watchlistLoadMore } = useInfiniteScroll({
    pageInfo: watchlistData?.listWatchlistPlayers?.pageInfo,
    fetchMore: watchlistFetchMore,
    networkStatus: watchlistNetworkStatus,
  });

  // Select the appropriate data source based on playerSource
  const combinedPlayersData = useMemo(():
    | KanbanListWatchlistPlayersQuery
    | KanbanListMyOrganizationsPlayersQuery
    | undefined => {
    if (playerSource === "roster") {
      return rosterData;
    }
    return watchlistData;
  }, [playerSource, rosterData, watchlistData]);

  // Select the appropriate loading state
  const finalPlayersLoading = useMemo(() => {
    if (playerSource === "roster") {
      return rosterLoading;
    }
    return watchlistLoading;
  }, [playerSource, rosterLoading, watchlistLoading]);

  // Select the appropriate error state
  const finalPlayersError = useMemo(() => {
    if (playerSource === "roster") {
      return rosterError;
    }
    return watchlistError;
  }, [playerSource, rosterError, watchlistError]);

  // Convert GraphQL data to player format
  const processedPlayers = useMemo(() => {
    // Handle both watchlist and roster data structures
    const edges =
      (combinedPlayersData as KanbanListWatchlistPlayersQuery | undefined)
        ?.listWatchlistPlayers?.edges ||
      (combinedPlayersData as KanbanListMyOrganizationsPlayersQuery | undefined)
        ?.listMyOrganizationsPlayers?.edges;

    if (!edges) {
      return [];
    }

    // Return KanbanPlayer objects directly from GraphQL data
    return edges.map(edge => {
      const player = edge.node;
      return {
        id: player.id,
        name: player.name,
        knownName: player.knownName || "",
        firstName: player.firstName || "",
        lastName: player.lastName || "",
        isManual: player.isManual,
        // SE-6952: the My Roster cards split their meta line on academy
        // membership, and the club segment falls back to the flat teamName.
        isAcademy: player.isAcademy,
        teamName: player.teamName || null,
        dateOfBirth: player.dateOfBirth || null,
        team: player.team
          ? {
              id: player.team.id,
              name: player.team.name,
            }
          : null,
        positions: player.positions || null,
        bioData: {
          ggPlayerKey: player.bioData?.ggPlayerKey || "",
          age: player.bioData?.age || null,
          gpr: player.bioData?.gpr || null,
          roleArchetype: player.bioData?.roleArchetype || null,
          minGeminiPlayerValuation:
            player.bioData?.minGeminiPlayerValuation || null,
          maxGeminiPlayerValuation:
            player.bioData?.maxGeminiPlayerValuation || null,
          contractExpires: player.bioData?.contractExpires || null,
          playerAgent: player.bioData?.playerAgent || null,
          customStats: player.bioData?.customStats || {},
        },
      };
    });
  }, [combinedPlayersData]);

  // Select the appropriate loadMore function based on playerSource
  const loadMore =
    playerSource === "roster" ? rosterLoadMore : watchlistLoadMore;

  return {
    // Processed data
    processedPlayers,

    // Loading/Error states
    finalPlayersLoading,
    finalPlayersError,

    // Pagination
    loadMore,
  };
};

export default usePlayerQuery;
